/**
 * GoogleSyncStatusBanner - Selo de status da sincronização com o Google Agenda
 *
 * Objetivo: deixar explícito, dentro da própria tela do calendário, se a conta
 * Google do usuário está realmente sincronizando. Antes desta versão o usuário
 * via "Conectado" no modal de integração enquanto nenhum calendário Google
 * aparecia na lista (falha silenciosa de provisionamento), sem nenhuma pista
 * sobre o que estava errado.
 *
 * Estados possíveis:
 *   - Sem conta conectada  -> convite discreto para conectar
 *   - Conectado e saudável -> conta + horário da última sincronização
 *   - Conectado com erro   -> mostra o último erro reportado pelo Google
 *   - Reparando            -> conta conectada porém sem calendário vinculado
 *                             (a reconciliação automática do worker resolve em
 *                             até ~1 minuto)
 *
 * RELEASE:
 *   2026-08-07 v1.0.0 - Versão inicial
 */

import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';
import { Button } from '@/components/ui/button';
import { useCalendarIntegration } from '@/hooks/useSchedulingEventTypes';
import { useGoogleCalendarConnect } from '@/hooks/useGoogleCalendarConnect';
import type { UserCalendar } from '@/hooks/useCalendars';

interface GoogleSyncStatusBannerProps {
  /** Calendários visíveis ao usuário (usado para detectar vínculo Google). */
  calendars: UserCalendar[];
}

export function GoogleSyncStatusBanner({ calendars }: GoogleSyncStatusBannerProps) {
  const { integration, isConnected, isLoading } = useCalendarIntegration();
  const { connect, isConnecting } = useGoogleCalendarConnect();

  // Enquanto a integração carrega não mostramos nada (evita "piscar" estados)
  if (isLoading) return null;

  // ---------------------------------------------------------------------------
  // Estado 1: nenhuma conta Google conectada
  // ---------------------------------------------------------------------------
  if (!isConnected || !integration) {
    return (
      <div className="rounded-md border bg-card px-2.5 py-2 flex items-center gap-2">
        <img src={googleCalendarIcon} alt="" className="h-4 w-4 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium leading-tight">Google Agenda</p>
          <p className="text-[10px] text-muted-foreground leading-tight">
            Nenhuma conta conectada
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[10px] px-2"
          onClick={() => connect()}
          disabled={isConnecting}
        >
          {isConnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Conectar'}
        </Button>
      </div>
    );
  }

  // Existe calendário local vinculado a esta integração?
  const linkedCalendar = calendars.find(
    (c) => c.google_integration_id === integration.id,
  );

  const lastSync = integration.last_sync_at
    ? formatDistanceToNow(new Date(integration.last_sync_at), {
        addSuffix: true,
        locale: ptBR,
      })
    : null;

  // ---------------------------------------------------------------------------
  // Estado 2: conta conectada porém sem calendário vinculado (auto-reparo)
  // ---------------------------------------------------------------------------
  if (!linkedCalendar) {
    return (
      <div className="rounded-md border border-amber-500/40 bg-card px-2.5 py-2 flex items-start gap-2">
        <RefreshCw className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5 animate-spin" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium leading-tight">
            Preparando sincronização
          </p>
          <p className="text-[10px] text-muted-foreground leading-tight break-all">
            {integration.provider_email} — o calendário será criado
            automaticamente em instantes.
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Estado 3: conectado com erro reportado na última sincronização
  // ---------------------------------------------------------------------------
  if (integration.last_sync_error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-card px-2.5 py-2 flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium leading-tight">
            Erro na sincronização
          </p>
          <p
            className="text-[10px] text-muted-foreground leading-tight line-clamp-2"
            title={integration.last_sync_error}
          >
            {integration.last_sync_error}
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Estado 4: conectado e saudável
  // ---------------------------------------------------------------------------
  return (
    <div className="rounded-md border bg-card px-2.5 py-2 flex items-start gap-2">
      <img src={googleCalendarIcon} alt="" className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium leading-tight flex items-center gap-1">
          Google Agenda
          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
        </p>
        <p className="text-[10px] text-muted-foreground leading-tight break-all">
          {integration.provider_email}
        </p>
        <p className="text-[10px] text-muted-foreground leading-tight">
          {integration.sync_enabled === false
            ? 'Sincronização desativada'
            : lastSync
              ? `Sincronizado ${lastSync}`
              : 'Aguardando primeira sincronização'}
        </p>
        {/* Link direto: deixa explícito EM QUAL conta Google os eventos são
            gravados, evitando o usuário procurar o evento em outra conta. */}
        <a
          href={`https://calendar.google.com/calendar/u/0/r?authuser=${encodeURIComponent(integration.provider_email ?? '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
        >
          Abrir no Google Agenda <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>
    </div>
  );
}

